import { FC, useMemo } from 'react';
import { cn } from '../../../../utils/cn';
import type { TherapistAppointment } from './types';
import { formatDate, getWeekDays, isToday } from './dateUtils';
import { AppointmentCard } from './AppointmentCard';

interface WeekViewProps {
  date: Date;
  appointments: TherapistAppointment[];
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const WeekView: FC<WeekViewProps> = ({ date, appointments }) => {
  const weekDays = getWeekDays(date);

  const appointmentsByDay = useMemo(() => {
    const byDay: Record<string, TherapistAppointment[]> = {};
    weekDays.forEach((day) => {
      const dateStr = formatDate(day);
      byDay[dateStr] = appointments
        .filter((apt) => apt.date === dateStr)
        .sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return byDay;
  }, [weekDays, appointments]);

  return (
    <div className="grid grid-cols-7 gap-2">
      {/* Header row */}
      {weekDays.map((day, index) => (
        <div
          key={day.toISOString()}
          className={cn(
            'text-center p-2 rounded-t-lg',
            isToday(day)
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-slate-100'
          )}
        >
          <div className="text-xs font-medium">{DAY_NAMES[index]}</div>
          <div className="text-lg font-bold">{day.getDate()}</div>
        </div>
      ))}
      {/* Appointments row */}
      {weekDays.map((day) => {
        const dateStr = formatDate(day);
        const dayAppointments = appointmentsByDay[dateStr] || [];
        return (
          <div
            key={`appointments-${dateStr}`}
            className={cn(
              'min-h-[200px] p-2 space-y-2 rounded-b-lg border',
              isToday(day)
                ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/10'
                : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'
            )}
          >
            {dayAppointments.length === 0 ? (
              <div className="text-xs text-gray-400 dark:text-slate-500 text-center py-4">
                No appointments
              </div>
            ) : (
              dayAppointments.map((apt) => (
                <AppointmentCard key={apt.id} appointment={apt} compact />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
};
